import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import orderBy from 'lodash/fp/orderBy'
import AddIcon from '@mui/icons-material/Add'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import { Box, Button, Accordion, AccordionDetails } from '@mui/material'
import { carePlanV2, CarePlanV2ListResponse, task as taskApi } from '@fireflyhealth/api'
import CarePlansModal from './CarePlansModal/CarePlansModal'
import { EmptyState, Loader, SectionCard, SegmentedControl } from '~/shared/ui'
import CarePlan from './CarePlan'
import { useMutation } from '~/shared/hooks/useMutation'
import { queueNotification } from '~/shared/notifications/actions'
import CarePlanActionModal from './CarePlansModal/CarePlanActionModal'
import NewCarePlanHeader from './NewCarePlanHeader'
import CaseBasedCarePlanContent from './CaseBasedCarePlanContent'
import CaseBasedCarePlanModal from './CaseBasedModal/CaseBasedCarePlanModal'
import { useEducationResources } from './CarePlanService'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'
import type { CarePlanItem, CarePlanApiResponse, MeasureStatus } from './carePlanTypes'

interface CarePlansProps {
  patient: PatientUser
}

const toCarePlanItem = (response: CarePlanApiResponse): CarePlanItem => response as CarePlanItem

const TABS = {
  active: 'active' as const,
  complete: 'complete' as const,
} as const

type TabValue = (typeof TABS)[keyof typeof TABS]

/**
 * Design-refresh Care Plans tab: the legacy container's care-plan list state,
 * task toggling, and all three modals verbatim, re-rendered with a segmented
 * Active/Inactive toggle and one SectionCard per care plan (the existing
 * accordion header/content satellites render inside the card).
 */
const CarePlans = (props: CarePlansProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [carePlans, setCarePlans] = useState<CarePlanV2ListResponse>([])
  const [modalCarePlan, setModalCarePlan] = useState<CarePlanItem | null>(null)
  const [tab, setTab] = useState<TabValue>(TABS.active)
  const dispatch = useDispatch()
  const providers = useSelector((state: RootState) => state.providers)
  const assigneeGroups = useSelector((state: RootState) => state.assigneeGroups)
  const me = useSelector((state: RootState) => state.me)

  const [modalCarePlanAction, setModalCarePlanAction] = useState<{
    carePlan: CarePlanItem
    action: string
    onCarePlanActionChange: (action: string) => void
  } | null>(null)
  const [edit, saveEdit] = useState<boolean>(false)

  const { loading: loadingCarePlans, handler: loadCarePlans } = useMutation(
    async () => {
      if (!props.patient.id) return []
      const { data } = await carePlanV2.list({ params: { patient: props.patient.id } })
      setCarePlans(data)
      return data
    },
    // Start in the loading state so the empty-state branch can't flash before
    // the mount-effect load resolves (carePlans is [] on the first render).
    { initialLoading: true },
  )

  const { data: educationResourceOptions } = useEducationResources()

  useEffect(() => {
    void loadCarePlans()
  }, [props.patient.id, loadCarePlans])

  /**
   * Filter care plans by checking status_category
   * @param active - whether to filter for active or inactive care plans
   */
  const getFilteredCarePlans = (active = true, carePlans: CarePlanV2ListResponse) => {
    if (active) {
      return carePlans.filter(
        carePlan =>
          carePlan.status_category !== 'complete' && carePlan.status_category !== 'deferred',
      )
    } else {
      return carePlans.filter(
        carePlan =>
          carePlan.status_category === 'complete' || carePlan.status_category === 'deferred',
      )
    }
  }

  const activeLength = useMemo(() => {
    return getFilteredCarePlans(true, carePlans).length
  }, [carePlans])

  const completeLength = useMemo(() => {
    return getFilteredCarePlans(false, carePlans).length
  }, [carePlans])

  // Sort care plans - last updated first for non case based and case based will be on the top
  const sortedCarePlans = useMemo(() => {
    const updateAtSortedCarePlans = orderBy(
      carePlan => carePlan.updated_at,
      ['desc'],
      getFilteredCarePlans(tab === TABS.active, carePlans),
    )

    return orderBy(
      carePlan => !!carePlan.is_case_based_care_plan,
      ['desc'],
      getFilteredCarePlans(tab === TABS.active, updateAtSortedCarePlans),
    )
  }, [carePlans, tab])

  const existingCarePlanTypeIds: { [key: number]: boolean } = (() => {
    const typeIds: { [key: number]: boolean } = {}
    for (const carePlan of carePlans) {
      if (carePlan.type_id) {
        typeIds[carePlan.type_id] = true
      }
    }
    return typeIds
  })()

  const openModal = (carePlan: CarePlanItem | null = null, editClick = false) => {
    setModalOpen(true)
    setModalCarePlan(carePlan)
    if (editClick) saveEdit(editClick)
    else saveEdit(false)
  }

  const closeModal = () => {
    setModalOpen(false)
    setModalCarePlan(null)
  }

  // Callback for modal actions. Add careplan if it's new, otherwise update existing.
  const onCareplanCreateOrUpdate = (updatedCarePlan: CarePlanItem | CarePlanApiResponse) => {
    const carePlanItem = toCarePlanItem(updatedCarePlan)
    let newCarePlans = [...carePlans]
    const carePlanIndex = newCarePlans.findIndex(c => c.id === carePlanItem.id)
    if (carePlanIndex > -1) {
      newCarePlans[carePlanIndex] = carePlanItem
    } else {
      newCarePlans = carePlans.concat(carePlanItem)
    }
    setCarePlans(newCarePlans)
  }

  // Local reducer for toggling tasks complete
  const toggleTaskComplete = async (
    carePlanId: number,
    taskId: number,
    payload: { is_complete: boolean },
  ) => {
    const { data: updatedTask } = await taskApi.partialUpdate({ id: taskId }, { data: payload })
    const newCarePlans = [...carePlans]
    const tasks = newCarePlans.find(c => c.id === carePlanId)?.tasks
    if (tasks) {
      const taskIndex = tasks.findIndex(t => t.id === taskId)
      tasks[taskIndex] = updatedTask
    }
    setCarePlans(newCarePlans)
  }

  const dispatchNotification = (notification: Parameters<typeof queueNotification>[0]) => {
    dispatch(queueNotification(notification))
  }

  const renderCarePlans = () => {
    if (loadingCarePlans) return <Loader />
    if (!sortedCarePlans?.length) {
      return (
        <EmptyState
          icon={<AssignmentTurnedInIcon />}
          title={tab === TABS.active ? 'No active care plans' : 'No inactive care plans'}
        />
      )
    }
    return sortedCarePlans.map((carePlan, i) => {
      let categorizedMeasureCollection: Record<
        MeasureStatus,
        typeof carePlan.measure_collection_measures
      > = {} as Record<MeasureStatus, typeof carePlan.measure_collection_measures>
      if (carePlan.measure_collection_measures) {
        categorizedMeasureCollection = carePlan.measure_collection_measures.reduce(
          (acc, mcm) => {
            let key: MeasureStatus
            for (const measureReport of mcm.measure_reports) {
              if (measureReport.is_care_gap) {
                key = 'Outstanding'
              } else {
                key = 'Complete'
              }
              if (key) {
                acc[key] ||= []
                acc[key].push(mcm)
              }
            }

            return acc
          },
          {} as Record<MeasureStatus, typeof carePlan.measure_collection_measures>,
        )
      }

      return (
        <SectionCard key={carePlan.id} disablePadding sx={{ mb: 2 }}>
          <Accordion
            defaultExpanded={i === 0}
            disableGutters
            sx={{
              boxShadow: 'none',
              bgcolor: 'transparent',
              '&::before': {
                backgroundColor: 'transparent',
              },
              '& .MuiButtonBase-root.MuiAccordionSummary-root': {
                minHeight: 'auto',
                cursor: 'default',
              },
              '& .MuiButtonBase-root.MuiAccordionSummary-root.Mui-focusVisible': {
                backgroundColor: 'transparent',
              },
              '& .MuiButtonBase-root.MuiAccordionSummary-root .MuiAccordionSummary-content': {
                margin: '6px 0',
                paddingTop: 0.75,
              },
            }}
          >
            <NewCarePlanHeader
              providers={providers}
              carePlan={carePlan}
              openModal={openModal}
              setModalCarePlanAction={setModalCarePlanAction}
              onCareplanCreateOrUpdate={onCareplanCreateOrUpdate}
              dispatchNotification={dispatchNotification}
              categorizedMeasureCollection={categorizedMeasureCollection}
            />
            <AccordionDetails>
              {carePlan.is_case_based_care_plan ? (
                <CaseBasedCarePlanContent
                  carePlan={carePlan}
                  categorizedMeasureCollection={categorizedMeasureCollection}
                />
              ) : (
                <CarePlan
                  patient={props.patient}
                  providers={providers}
                  openModal={openModal}
                  toggleTaskComplete={toggleTaskComplete}
                  carePlan={carePlan}
                  hideShowCompletedToggle={tab == TABS.complete}
                  onCareplanCreateOrUpdate={onCareplanCreateOrUpdate}
                  dispatchNotification={dispatchNotification}
                  setModalCarePlanAction={setModalCarePlanAction}
                  assigneeGroups={assigneeGroups}
                />
              )}
            </AccordionDetails>
          </Accordion>
        </SectionCard>
      )
    })
  }

  const carePlansModal = () => {
    return (
      <>
        {modalCarePlan?.is_case_based_care_plan ? (
          <CaseBasedCarePlanModal
            carePlan={modalCarePlan}
            closeModal={closeModal}
            onCareplanCreateOrUpdate={onCareplanCreateOrUpdate}
            educationResourceOptions={educationResourceOptions ?? []}
            edit={edit}
          />
        ) : (
          <CarePlansModal
            closeModal={closeModal}
            carePlan={modalCarePlan}
            open={modalOpen}
            patient={props.patient}
            me={me}
            handleModalAction={onCareplanCreateOrUpdate}
            existingCarePlanTypeIds={existingCarePlanTypeIds}
          />
        )}
        {modalCarePlanAction == null ? null : (
          <CarePlanActionModal
            carePlan={modalCarePlanAction.carePlan}
            onCarePlanActionChange={modalCarePlanAction.onCarePlanActionChange}
            action={modalCarePlanAction.action}
            closeModal={() => setModalCarePlanAction(null)}
          />
        )}
      </>
    )
  }

  return (
    <Box sx={{ px: 3, pt: 1.5, pb: 5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <SegmentedControl
          aria-label="Care plan status"
          options={[
            { value: TABS.active, label: `Active (${activeLength})` },
            { value: TABS.complete, label: `Inactive (${completeLength})` },
          ]}
          value={tab}
          onChange={setTab}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ flexShrink: 0 }}
          onClick={() => openModal(null)}
        >
          Add care plan
        </Button>
      </Box>
      {renderCarePlans()}
      {carePlansModal()}
    </Box>
  )
}

export default CarePlans
